import type {
    Column,
    Filter,
    Order,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    Button,
    Icon,
    Modal,
    SearchInput,
    Text,
    TextButton,
} from "@amzn/storm-ui";
import {
    MultiSelect,
    MultiSelectItem,
    MultiSelectItemGroup,
} from "@amzn/storm-ui-multi-select";
import {
    useAsync,
    useDebouncedEffect,
    useFirstMountState,
    useToggle,
} from "@react-hookz/web";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { InView } from "react-intersection-observer";
import * as R from "remeda";
import styled from "styled-components";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { CLIENT_ID } from "../../config/constants";
import {
    useClearDimensionValueOnDependencyChange,
    useGetDimensionDisable,
    useSetDimensionValue,
    useUpdateStatusOnChange,
} from "../../hooks/dimension/dimension.hooks";
import { authStore } from "../../store/auth";
import {
    dimensionHydrationStore,
    HydrationState,
    useDimensionHydration,
} from "../../store/dimension.hydration";
import {
    DimensionStatus,
    dimensionStatusStore,
} from "../../store/dimension.status";
import type { MultiSelectDimensionValue } from "../../store/dimension.value";
import { dimensionValueStore } from "../../store/dimension.value";
import { Comparator, type InputParameterFilter } from "../../types/common";
import { DimensionTypes } from "../../types/dimensions";
import type {
    DimensionConfiguration,
    DimensionTemplate,
} from "../../types/dimensions";
import { isLoading } from "../../utils/api.utils";
import { buildRequestParameter } from "../../utils/app.utils";
import { createSimultaneousRequests } from "../../utils/async.utils";
import { stringBuilderWithData } from "../../utils/common.utils";
import { LabelEndComponent } from "../common/LabelEndComponent";

import * as DropdownDimensionHelpers from "./DropdownDimensionHelpers";
import {
    BulkActionsContainer,
    SearchBarContainer,
} from "./MultiSelectDimension.styles";
import { BasicDimensionBox } from "./common/components";
import { MULTISELECT_ERRORS } from "./dimensions.errors";

const MAX_ITEMS = 1000;

const enum MODAL_STATES {
    FETCH_ALL_AND_APPLY = "FETCH_ALL_AND_APPLY",
    AT_MAX = "AT_MAX",
}
const DIALOG_PROMPTS = {
    [MODAL_STATES.FETCH_ALL_AND_APPLY]: (numItems: number) =>
        `Are you sure you want to select all items including ${numItems} not yet displayed`,
    [MODAL_STATES.AT_MAX]: (max) =>
        `This action cannot be completed as there is a maximum selection of ${max} items.`,
};

const CancelButton = styled(TextButton)`
    margin-right: ${({ theme }) => theme.spacing.small.default};
`;
CancelButton.displayName = "CancelButton";

export interface MultiSelectConfiguration extends DimensionConfiguration {
    inputParameterFilters?: InputParameterFilter[];
    inputSource: string;
    delimiter?: string;
    columns: Column[];
    orderBy?: Order[];
    groupBy?: string[];
    dropdownItemMapper: {
        value?: string;
        /**
         * @deprecated Use value instead.
         */
        valueFormat?: string;
        detailsFormat?: string;
        labelFormat: string;
        subTextFormat?: string;
    };
    attachDatabaseResponseToMetadata?: boolean;
    enableBulkActions?: boolean;
    useTypeaheadSearchWithColumn?: string;
    useSubstringSearchWithColumn?: string;
    queryDistinct?: boolean;
    maxItemsSelected?: number;
    explodeSelectedValues?: boolean;
    writeSelectedLabelsToRequestParameter?: boolean;
}

export interface MultiSelectDimensionProps extends DimensionTemplate {
    type: DimensionTypes.MULTISELECT;
    configuration: MultiSelectConfiguration;
}

export interface MultiSelectDimensionItem {
    value: string;
    label: string;
    subText?: string;
    details?: string;
    _metadata?: Record<string, string>;
}

export const MultiSelectDimension: React.FunctionComponent<
    MultiSelectDimensionProps
> = (props) => {
    const {
        id,
        dependencies,
        requestParameterMap,
        optional,
        dimensionMetadata,
    } = props;
    const {
        columns,
        delimiter,
        dropdownItemMapper,
        groupBy,
        inputParameterFilters,
        inputSource,
        isAdvertiserScoped,
        label,
        orderBy,
        tooltip,
        enableBulkActions = true,
        useTypeaheadSearchWithColumn,
        useSubstringSearchWithColumn,
        queryDistinct = true,
        maxItemsSelected,
        attachDatabaseResponseToMetadata,
        explodeSelectedValues = false,
        writeSelectedLabelsToRequestParameter = false,
    } = props.configuration;

    const [warnings, setWarnings] = React.useState<string[]>([]);

    const [stage] = useStore(authStore, (state) => [state.settings.stage]);

    React.useEffect(() => {
        if (dropdownItemMapper.valueFormat) {
            setWarnings((prev) => {
                return [
                    ...prev,
                    "dropdownItemMapper.valueFormat will be deprecated, see multi-select dimension specs for more information: https://w.amazon.com/bin/view/ALIGHT/CDB/UsingCDB/DimensionsSpecifications/MultiselectDropdownDimension/ValueFormatDeprecation",
                ];
            });
        }
    }, [dropdownItemMapper.valueFormat]);

    if (maxItemsSelected && maxItemsSelected > MAX_ITEMS) {
        throw new Error(
            `configuration.maxItemsSelected must be less than or equal to ${MAX_ITEMS}`,
        );
    }

    if (dropdownItemMapper.value && dropdownItemMapper.valueFormat) {
        throw new Error(
            MULTISELECT_ERRORS.CONFIGURATION.BOTH_VALUE_AND_VALUEFORMAT,
        );
    } else if (!dropdownItemMapper.value && !dropdownItemMapper.valueFormat) {
        throw new Error(
            MULTISELECT_ERRORS.CONFIGURATION.MISSING_VALUE_IN_DROPDOWN_ITEM_MAPPER,
        );
    }

    const getValueForItem = React.useCallback(
        (e: Record<string, string>) => {
            if (dropdownItemMapper.valueFormat) {
                return stringBuilderWithData(dropdownItemMapper.valueFormat, e);
            } else if (dropdownItemMapper.value) {
                if (e[dropdownItemMapper.value]) {
                    return e[dropdownItemMapper.value];
                } else {
                    throw new Error(
                        MULTISELECT_ERRORS.RUNTIME.ITEM_VALUE_CANNOT_BE_FOUND(
                            dropdownItemMapper.value,
                            e,
                        ),
                    );
                }
            } else {
                throw new Error(
                    MULTISELECT_ERRORS.CONFIGURATION.MISSING_VALUE_IN_DROPDOWN_ITEM_MAPPER,
                );
            }
        },
        [dropdownItemMapper],
    );

    const processObjectToMultiSelectItem = React.useCallback(
        (i: Record<string, string>): MultiSelectDimensionItem => {
            const item: MultiSelectDimensionItem = {
                value: getValueForItem(i),
                label: stringBuilderWithData(dropdownItemMapper.labelFormat, i),
            };
            if (dropdownItemMapper.subTextFormat) {
                item.subText = stringBuilderWithData(
                    dropdownItemMapper.subTextFormat,
                    i,
                );
            }
            if (dropdownItemMapper.detailsFormat) {
                item.details = stringBuilderWithData(
                    dropdownItemMapper.detailsFormat,
                    i,
                );
            }
            if (attachDatabaseResponseToMetadata) {
                item._metadata = i;
            }
            return item;
        },
        [dropdownItemMapper],
    );

    const enableSearchInput =
        R.isTruthy(useTypeaheadSearchWithColumn) ||
        R.isTruthy(useSubstringSearchWithColumn);

    const dependencyValues = useStore(dimensionValueStore, (state) =>
        dependencies.map((d) => state[d]?.data),
    );

    const dependencyStatuses = useStore(dimensionStatusStore, (state) =>
        dependencies.map((d) => state[d]),
    );

    const allDependencyValuesValid = useMemo(() => {
        for (let i = 0; i < dependencies.length; i++) {
            if (dependencyStatuses[i] !== DimensionStatus.SUCCESS) {
                return false;
            }
            if (
                dependencyValues[i] === undefined ||
                !R.isTruthy(dependencyValues[i]?.value)
            ) {
                return false;
            }
        }
        return true;
    }, [dependencyValues, dependencyStatuses]);

    const disabled =
        useGetDimensionDisable(dependencies) || !allDependencyValuesValid;

    const [
        { selectedItems = [], selectedItemsObjects = [] },
        setDimensionValue,
    ] = useSetDimensionValue<MultiSelectDimensionValue>(id, (value) => {
        let output = buildRequestParameter(
            requestParameterMap ?? {
                value: `${id}_id`,
                selectedItemsObjects: `${id}_selectedObjects`,
            },
        )(value);

        const prefix =
            requestParameterMap && requestParameterMap["value"]
                ? requestParameterMap["value"]
                : `${id}_id`;

        if (explodeSelectedValues) {
            const explodedValues = value.selectedItemsObjects.reduce(
                (prev, item, index) => {
                    return {
                        ...prev,
                        [`${prefix}_${index}`]: item.value,
                    };
                },
                {},
            );
            output = {
                ...output,
                ...explodedValues,
            };
        }

        if (writeSelectedLabelsToRequestParameter) {
            const selectedLabels = JSON.stringify(
                value.selectedItemsObjects.map((item) => item.label),
            );
            output[`${prefix}_selectedLabels`] = selectedLabels;
        }

        return output;
    });

    const [showOnlySelected, toggleShowOnlySelected] = useToggle(false);

    const [isModalOpen, toggleModal] = useToggle(false);
    const [modalDescription, setModalDescription] = useState("");
    const [modalState, setModalState] = useState(
        MODAL_STATES.FETCH_ALL_AND_APPLY,
    );

    const [search, setSearch] = React.useState("");

    const searchFilter = useMemo(() => {
        if (search.length <= 0) {
            return undefined;
        } else if (R.isTruthy(useTypeaheadSearchWithColumn)) {
            return {
                comparator: Comparator.ILIKE,
                key: useTypeaheadSearchWithColumn || "",
                value: `${search}%`,
            };
        } else if (R.isTruthy(useSubstringSearchWithColumn)) {
            return {
                comparator: Comparator.ILIKE,
                key: useSubstringSearchWithColumn || "",
                value: `%${search}%`,
            };
        }
        return undefined;
    }, [search]);

    const dropdownFilterQueryParameter = useMemo(
        () =>
            DropdownDimensionHelpers.getDropdownFilters(
                inputParameterFilters,
                searchFilter,
                isAdvertiserScoped,
            ),
        [...dependencyValues, search],
    );

    const [asyncDropdownValuesState, asyncDropdownValuesAction] = useAsync(
        async () => {
            const response = await (
                await getCdbApiClient()
            ).getDropdownValues({
                datasetName: inputSource,
                additionalFilters: dropdownFilterQueryParameter,
                columns,
                orderBy,
                groupBy,
                pageSize: 100,
                pageToken: asyncDropdownValuesState.result.nextToken || 0,
                queryDistinct,
                metadata: {
                    ...dimensionMetadata,
                    dimensionIdentifier: id,
                    clientId: CLIENT_ID,
                },
            });

            if (
                asyncDropdownValuesState.result.nextToken &&
                Array.isArray(asyncDropdownValuesState.result.data) &&
                Array.isArray(response.data)
            ) {
                response.data = [
                    ...asyncDropdownValuesState.result.data,
                    ...response.data,
                ];
            }

            if (
                asyncDropdownValuesState.result?.nextToken ===
                response.nextToken
            ) {
                response.nextToken = undefined;
            }

            return {
                ...response,
                fetchedTimestamp: Date.now().valueOf(),
            };
        },
        {
            data: [],
            maxResults: 0,
            nextToken: undefined,
            fetchedTimestamp: Date.now().valueOf(),
        },
    );

    const items: MultiSelectDimensionItem[] = useMemo(() => {
        const dropdownItems = (asyncDropdownValuesState.result.data || []).map(
            (i) => processObjectToMultiSelectItem(i),
        );
        selectedItemsObjects.forEach((i) => {
            if (!dropdownItems.find((item) => item.value === i.value)) {
                dropdownItems.push(i);
            }
        });
        return dropdownItems;
    }, [
        asyncDropdownValuesState.result.data,
        asyncDropdownValuesState.result.fetchedTimestamp,
        asyncDropdownValuesState.result.maxResults,
    ]);

    const [requiresHydration] = useStore(dimensionHydrationStore, (state) => [
        state.dimensionHydrationStatus[id] === HydrationState.PENDING,
    ]);

    useEffect(() => {
        if (!disabled && !requiresHydration) {
            asyncDropdownValuesState.result.data = []; // need to reset this to 0 get first page of filtered response
            asyncDropdownValuesState.result.nextToken = undefined; // need to reset this to 0 get first page of filtered response
            asyncDropdownValuesState.result.fetchedTimestamp =
                Date.now().valueOf(); // need to reset this to 0 get first page of filtered response
            void asyncDropdownValuesAction.execute();
        }
    }, [disabled, ...dependencyValues]);

    useClearDimensionValueOnDependencyChange(id, dependencies);

    useUpdateStatusOnChange(id, (data: MultiSelectDimensionValue) => {
        if (optional) {
            return DimensionStatus.SUCCESS;
        }
        if (data.selectedItems && data.selectedItems.length > 0) {
            return DimensionStatus.SUCCESS;
        }
        return DimensionStatus.EMPTY;
    });

    const bulkActionHandler = useCallback(
        (actionValue: string) => {
            if (actionValue === "add") {
                const { maxResults, data } = asyncDropdownValuesState.result;
                // check if there adding more elements would result in > max items
                if (
                    maxResults &&
                    selectedItems.length + maxResults >
                        (maxItemsSelected || MAX_ITEMS)
                ) {
                    setModalDescription(
                        DIALOG_PROMPTS.AT_MAX(maxItemsSelected || MAX_ITEMS),
                    );
                    setModalState(MODAL_STATES.AT_MAX);
                    toggleModal(true);
                } // check if there are unfetched elements
                else if (maxResults && data && data.length < maxResults) {
                    setModalDescription(
                        DIALOG_PROMPTS.FETCH_ALL_AND_APPLY(
                            maxResults - data.length,
                        ),
                    );
                    setModalState(MODAL_STATES.FETCH_ALL_AND_APPLY);
                    toggleModal(true);
                } else {
                    const newSelectedItems = R.uniqueWith(
                        [...selectedItems, ...items.map((i) => i.value)],
                        R.isDeepEqual,
                    );
                    const newSelectedItemsObjects = R.uniqueWith(
                        [...selectedItemsObjects, ...items],
                        R.isDeepEqual,
                    );
                    const mainValue = delimiter
                        ? newSelectedItems.join(delimiter)
                        : JSON.stringify(newSelectedItems);

                    const dimensionData: MultiSelectDimensionValue = {
                        type: DimensionTypes.MULTISELECT,
                        value: mainValue,
                        selectedItems: newSelectedItems,
                        selectedItemsObjects: newSelectedItemsObjects,
                    };

                    // If custom requestParameterMap is provided, also store the main value under the custom parameter name
                    if (requestParameterMap && requestParameterMap["value"]) {
                        dimensionData[requestParameterMap["value"]] = mainValue;
                    }

                    if (explodeSelectedValues) {
                        const prefix =
                            requestParameterMap && requestParameterMap["value"]
                                ? requestParameterMap["value"]
                                : `${id}_id`;
                        newSelectedItemsObjects.forEach((item, index) => {
                            dimensionData[`${prefix}_${index}`] = item.value;
                        });
                    }

                    setDimensionValue(dimensionData);
                }
            } else if (actionValue === "clear") {
                const dimensionData: MultiSelectDimensionValue = {
                    type: DimensionTypes.MULTISELECT,
                    value: "",
                    selectedItems: [],
                    selectedItemsObjects: [],
                };

                // If custom requestParameterMap is provided, also store the main value under the custom parameter name
                if (requestParameterMap && requestParameterMap["value"]) {
                    dimensionData[requestParameterMap["value"]] = "";
                }

                if (explodeSelectedValues) {
                    const prefix =
                        requestParameterMap && requestParameterMap["value"]
                            ? requestParameterMap["value"]
                            : `${id}_id`;
                    // Clear any existing exploded values
                    Object.keys(
                        dimensionValueStore.getState()[id]?.data || {},
                    ).forEach((key) => {
                        if (key.startsWith(prefix + "_")) {
                            delete dimensionData[key];
                        }
                    });
                }

                setDimensionValue(dimensionData);
            } else if (actionValue === "toggleSelectedVisible") {
                toggleShowOnlySelected();
            }
        },
        [setDimensionValue],
    );

    const fetchNextPageHandler = useCallback(() => {
        if (asyncDropdownValuesState.result.nextToken) {
            void asyncDropdownValuesAction.execute();
        }
    }, [asyncDropdownValuesState]);

    const labelEndProps = { tooltip, optional };
    const renderLabelEnd = <LabelEndComponent {...labelEndProps} />;

    const isFirstMount = useFirstMountState();
    useDebouncedEffect(
        () => {
            if (!disabled && !isFirstMount) {
                asyncDropdownValuesState.result.data = []; // need to reset this to 0 get first page of filtered response
                asyncDropdownValuesState.result.nextToken = undefined; // need to reset this to 0 get first page of filtered response
                asyncDropdownValuesState.result.fetchedTimestamp =
                    Date.now().valueOf(); // need to reset this to 0 get first page of filtered response
                void asyncDropdownValuesAction.execute();
            }
        },
        [search],
        500,
    );

    const multiSelectItems = useMemo(() => {
        const displayedItems = showOnlySelected
            ? selectedItemsObjects.filter((i) =>
                  i.value.toLowerCase().startsWith(search.toLowerCase()),
              )
            : items;
        const set = new Set(selectedItems);
        return displayedItems.map((i, index) => (
            <MultiSelectItem
                name={i.label}
                value={i.value}
                key={index}
                disabled={
                    set.size >= (maxItemsSelected || MAX_ITEMS) &&
                    !set.has(i.value)
                }
            >
                <Text>{i.label}</Text>
                <Text style={{ color: "grey" }} size="xsmall">
                    {i.subText}
                </Text>
                {items.length - 1 === index && (
                    <InView
                        as="span"
                        onChange={(inView) => inView && fetchNextPageHandler()}
                    ></InView>
                )}
            </MultiSelectItem>
        ));
    }, [
        asyncDropdownValuesState.result.fetchedTimestamp,
        items,
        selectedItems,
        showOnlySelected,
    ]);

    const [fetchRemainingAndSetState, fetchRemainingAndSetAction] = useAsync(
        async () => {
            const response = await (
                await getCdbApiClient()
            ).getDropdownValues({
                datasetName: inputSource,
                additionalFilters: dropdownFilterQueryParameter,
                columns,
                orderBy,
                groupBy,
                pageSize: MAX_ITEMS,
                pageToken: 0,
                queryDistinct,
                metadata: {
                    ...dimensionMetadata,
                    dimensionIdentifier: id,
                    clientId: CLIENT_ID,
                },
            });
            const newSelectedItems = [
                ...selectedItems,
                ...(response.data ?? []).map((e) => getValueForItem(e)),
            ];

            // need to build item objects for selected items
            const newSelectedItemsObjects = [
                ...selectedItemsObjects,
                ...(response.data ?? []).map((e) =>
                    processObjectToMultiSelectItem(e),
                ),
            ];
            const dimensionData: any = {
                type: DimensionTypes.MULTISELECT,
                value: delimiter
                    ? newSelectedItems.join(delimiter)
                    : JSON.stringify(newSelectedItems),
                selectedItems: newSelectedItems,
                selectedItemsObjects: newSelectedItemsObjects,
            };

            if (explodeSelectedValues) {
                const prefix =
                    requestParameterMap && requestParameterMap["value"]
                        ? requestParameterMap["value"]
                        : `${id}_id`;
                newSelectedItemsObjects.forEach((item, index) => {
                    dimensionData[`${prefix}_${index}`] = item.value;
                });
            }

            setDimensionValue(dimensionData);
            toggleModal();
        },
    );

    const Footer = () => (
        <>
            <CancelButton onClick={toggleModal}>Cancel</CancelButton>
            <Button
                onClick={() => {
                    if (modalState === MODAL_STATES.FETCH_ALL_AND_APPLY) {
                        void fetchRemainingAndSetAction.execute();
                    } else if (modalState === MODAL_STATES.AT_MAX) {
                        toggleModal(false);
                    }
                }}
                variant="primary"
                loading={fetchRemainingAndSetState.status === "loading"}
            >
                Confirm
            </Button>
        </>
    );

    useDimensionHydration(
        id,
        dependencies,
        async (hydrationPayload) => {
            // first construct value payload
            const payloadValues = {
                valueIdLookup: (() => {
                    if (requestParameterMap) {
                        return requestParameterMap.value;
                    } else {
                        return `${id}_id`;
                    }
                })(),
            };

            if (!hydrationPayload[payloadValues.valueIdLookup] && !optional) {
                throw new Error(
                    MULTISELECT_ERRORS.HYDRATION.VALUE_NOT_AVAILABLE_IN_PAYLOAD,
                );
            }

            let selectedValues = [];
            try {
                if (delimiter) {
                    selectedValues =
                        hydrationPayload[payloadValues.valueIdLookup].split(
                            delimiter,
                        );
                } else {
                    selectedValues = JSON.parse(
                        hydrationPayload[payloadValues.valueIdLookup],
                    );
                }
            } catch (err) {
                if (optional) {
                    return;
                }
                throw new Error(
                    MULTISELECT_ERRORS.HYDRATION.PAYLOAD_VALUE_FAILED_PARSE,
                );
            }

            if (dropdownItemMapper.valueFormat || !dropdownItemMapper.value) {
                throw new Error(
                    MULTISELECT_ERRORS.HYDRATION.VALUE_NOT_IN_DROPDOWN_ITEM_MAPPER,
                );
            }

            if (selectedValues.length > 0) {
                if (selectedValues.length > 200) {
                    throw new Error(
                        MULTISELECT_ERRORS.HYDRATION.TOO_MANY_ITEMS_SELECTED,
                    );
                }

                // look up items
                const foundItems = (
                    await createSimultaneousRequests(
                        selectedValues,
                        async (v) => {
                            const valueFilter: Filter = {
                                key: dropdownItemMapper.value,
                                comparator: Comparator.EQUALS,
                                value: v,
                            };
                            return (
                                await (
                                    await getCdbApiClient()
                                ).getDropdownValues({
                                    datasetName: inputSource,
                                    additionalFilters: [
                                        ...(dropdownFilterQueryParameter ?? []),
                                        valueFilter,
                                    ],
                                    columns,
                                    orderBy,
                                    groupBy,
                                    pageSize: 100,
                                    pageToken: 0,
                                    queryDistinct,
                                    metadata: {
                                        ...dimensionMetadata,
                                        dimensionIdentifier: id,
                                        clientId: CLIENT_ID,
                                    },
                                })
                            ).data
                                ?.map((i) => processObjectToMultiSelectItem(i))
                                .find((e) => e.value === v);
                        },
                    )
                ).filter((e) => e !== undefined) as MultiSelectDimensionItem[]; // typescript cannot correct infer type here;

                if (foundItems.length > 0) {
                    const v = foundItems.map((e) => e.value);
                    const mainValue = delimiter
                        ? foundItems.join(delimiter)
                        : JSON.stringify(v);

                    const dimensionData: MultiSelectDimensionValue = {
                        type: DimensionTypes.MULTISELECT,
                        value: mainValue,
                        selectedItems: v as any, // data will always be string,
                        selectedItemsObjects: foundItems, // need to locate all items selected
                    };

                    // If custom requestParameterMap is provided, also store the main value under the custom parameter name
                    if (requestParameterMap && requestParameterMap["value"]) {
                        dimensionData[requestParameterMap["value"]] = mainValue;
                    }

                    if (explodeSelectedValues) {
                        const prefix =
                            requestParameterMap && requestParameterMap["value"]
                                ? requestParameterMap["value"]
                                : `${id}_id`;
                        foundItems.forEach((item, index) => {
                            dimensionData[`${prefix}_${index}`] = item.value;
                        });
                    }

                    setDimensionValue(dimensionData);
                    void asyncDropdownValuesAction.execute();
                } else {
                    throw new Error(
                        MULTISELECT_ERRORS.HYDRATION.NO_MATCHING_ITEMS_FOUND,
                    );
                }

                if (foundItems.length !== selectedValues.length) {
                    throw new Error(
                        MULTISELECT_ERRORS.HYDRATION.SOME_ITEMS_NOT_FOUND,
                    );
                }
            } else {
                // execute when possible, just to get the dropdowns
                void asyncDropdownValuesAction.execute();
            }
        },
        () => {
            // fetches dropdown values normally if hydration fails
            void asyncDropdownValuesAction.execute();
        },
    );

    return (
        <BasicDimensionBox>
            <MultiSelect
                label={label}
                data-testid={`${id}-multiselect`}
                name={`multi-select-${id}`}
                selectedLabelOverride={
                    isLoading(asyncDropdownValuesState.status)
                        ? "Loading"
                        : undefined
                }
                loading={
                    isLoading(asyncDropdownValuesState.status) &&
                    search.length === 0 &&
                    asyncDropdownValuesState.result.nextToken === undefined
                }
                disabled={disabled}
                fullWidth={true}
                onChange={(v) => {
                    const allItems = [
                        ...selectedItemsObjects.filter((i) =>
                            v.includes(i.value),
                        ),
                        ...items.filter((i) => v.includes(i.value)),
                    ];
                    const allSelectedItems = R.uniqueWith(
                        allItems,
                        R.isDeepEqual,
                    );
                    const mainValue = delimiter
                        ? v.join(delimiter)
                        : JSON.stringify(v);

                    const dimensionData: MultiSelectDimensionValue = {
                        type: DimensionTypes.MULTISELECT,
                        value: mainValue,
                        selectedItems: v as any, // data will always be string,
                        selectedItemsObjects: allSelectedItems, // need to locate all items selected
                    };

                    // If custom requestParameterMap is provided, also store the main value under the custom parameter name
                    if (requestParameterMap && requestParameterMap["value"]) {
                        dimensionData[requestParameterMap["value"]] = mainValue;
                    }

                    if (explodeSelectedValues) {
                        const prefix =
                            requestParameterMap && requestParameterMap["value"]
                                ? requestParameterMap["value"]
                                : `${id}_id`;
                        allSelectedItems.forEach((item, index) => {
                            dimensionData[`${prefix}_${index}`] = item.value;
                        });
                    }

                    setDimensionValue(dimensionData);
                }}
                onOverrideLabel={(_, selectedValues): string => {
                    if (selectedValues.length === 0) {
                        return "Select Items";
                    }
                    return selectedItemsObjects
                        .map((item) => item.label)
                        .join(", ");
                }}
                renderLabelEnd={renderLabelEnd}
                selectedValues={selectedItems}
                secondaryViewCloseButtonLabel="Done"
                withBulkActionPanel={
                    enableBulkActions ? (
                        <BulkActionsContainer>
                            <div style={{ display: "flex", gap: 12 }}>
                                <TextButton
                                    onClick={() => bulkActionHandler("add")}
                                >
                                    Add all
                                </TextButton>
                                <TextButton
                                    onClick={() => bulkActionHandler("clear")}
                                >
                                    Clear all
                                </TextButton>
                                <TextButton
                                    onClick={() =>
                                        bulkActionHandler(
                                            "toggleSelectedVisible",
                                        )
                                    }
                                >
                                    Show{" "}
                                    {showOnlySelected ? "all" : "only selected"}
                                </TextButton>
                            </div>
                            <div style={{ whiteSpace: "nowrap" }}>
                                <Text color="tertiary" inline>
                                    {`${selectedItems.length} selected `}
                                </Text>
                                <Text color="tertiary" inline>
                                    {`${asyncDropdownValuesState.result.maxResults} Items`}
                                </Text>
                            </div>
                        </BulkActionsContainer>
                    ) : undefined
                }
                withSearchPanel={
                    enableSearchInput ? (
                        <SearchBarContainer>
                            <SearchInput
                                id="search-multi-select"
                                placeholder="Search"
                                fullWidth
                                value={search}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setSearch(e.target.value);
                                }}
                                onClear={() => setSearch("")}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                    if (e.key === " ") {
                                        e.stopPropagation();
                                    }
                                }}
                            />
                        </SearchBarContainer>
                    ) : undefined
                }
            >
                {isLoading(asyncDropdownValuesState.status) &&
                asyncDropdownValuesState.result.nextToken === undefined ? (
                    <div
                        data-testid="loading-spinner"
                        role="alert"
                        aria-live="assertive"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Icon type="spinner" size="base" />
                    </div>
                ) : (
                    <MultiSelectItemGroup label="">
                        {multiSelectItems}
                        {isLoading(asyncDropdownValuesState.status) && (
                            <div
                                data-testid="loading-spinner"
                                role="alert"
                                aria-live="assertive"
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                }}
                            >
                                <Icon type="spinner" size="base" />
                            </div>
                        )}
                        {items.length === 0 &&
                            !isLoading(asyncDropdownValuesState.status) && (
                                <MultiSelectItem
                                    name="none"
                                    value="none"
                                    disabled
                                >
                                    no items available
                                </MultiSelectItem>
                            )}
                    </MultiSelectItemGroup>
                )}
            </MultiSelect>
            {/* not showing deprecation warnings until fully released */}
            {/* {warnings.length > 0 && stage !== "prod" && (
            <div>
                <Alert
                    data-testid={`${id}-warning`}
                    variant="warning"
                    style={{ margin: "10px 0px" }}
                >
                    {warnings.map((w, i) => (
                        <Text key={i}>{w}</Text>
                    ))}
                </Alert>
            </div>
        )} */}
            <Modal
                isOpen={isModalOpen}
                header={`${label} Confirmation`}
                footer={<Footer />}
                onClose={toggleModal}
            >
                <Text>{modalDescription}</Text>
            </Modal>
        </BasicDimensionBox>
    );
};
